import { useQuery } from "@apollo/client";
import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import Tabs from "components/ui/tabs/Tabs";
import type { ITab } from "components/ui/tabs/types";
import { palette } from "lib/miolo";
import AmericanIssuerDividendsQuery from "lib/queries/AmericanIssuerDividendsQuery";
import type {
  IAmericanIssuerDividendsQuery,
  IAmericanIssuerDividendsQueryVariables,
} from "lib/queries/__generated__/AmericanIssuerDividendsQuery.generated";
import { useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { ISecurityCountry } from "types/graphqlTypes";
import ContentSectionBreadcrumbs from "../ContentSectionBreadcrumbs";
import AmericanDividendsPaidTabContet from "../content/AmericanDividendsPaidTabContet";
import AmericanSpecialDividendsPaidTabContet from "../content/AmericanSpecialDividendsPaidTabContet";
import AmericanStockDividendsPaidTabContet from "../content/AmericanStockDividendsPaidTabContet";
import AmericanDividendsPaidSummary from "../summary/AmericanDividendsPaidSummary";
import StockDividendSummary from "../summary/StockDividendSummary";

enum DividendsTab {
  Dividends = "DIVIDENDS",
  SpecialDividends = "SPECIAL_DIVIDENDS",
  StockDividends = "STOCK_DIVIDENDS",
}

interface IAmericanIssuerDividendsCollapsibleProps {
  tickerSymbol: string;
  isExpanded: boolean;
  onToggleCollapsed: () => void;
}

export default function AmericanIssuerDividendsCollapsible({
  tickerSymbol,
  isExpanded,
  onToggleCollapsed,
}: IAmericanIssuerDividendsCollapsibleProps) {
  const [filterDividendsByYear, setFilterDividendsByYear] = useState<number | null>(null);

  const { data, previousData, loading } = useQuery<
    IAmericanIssuerDividendsQuery,
    IAmericanIssuerDividendsQueryVariables
  >(AmericanIssuerDividendsQuery, {
    variables: {
      tickerSymbol,
      securityCountry: ISecurityCountry.Us,
      filterByExcludingFrom: filterDividendsByYear,
      filterByExcludingTo: filterDividendsByYear,
    },
  });

  if (!data && !previousData && loading) {
    return (
      <div className="flex w-full items-center justify-center">
        <ClipLoader size={28} color={palette.royalBlue} loading={true} />
      </div>
    );
  }

  const americanDividends = data ?? previousData;

  if (!americanDividends) {
    return null;
  }

  const { dividendsPaid, americanIssuerSpecialDividendsPaid, stockDividendsPaid } = americanDividends;

  const hasDividends = dividendsPaid.nodes.length > 0;
  const hasSpecialDividends = americanIssuerSpecialDividendsPaid.length > 0;
  const hasStockDividends = stockDividendsPaid.length > 0;

  const dividendsTabs: ITab[] = [
    {
      id: DividendsTab.Dividends,
      title: "Dividends",
      isHidden: !hasDividends,
    },
    {
      id: DividendsTab.SpecialDividends,
      title: "Special Dividends",
      isHidden: !hasSpecialDividends,
    },
    {
      id: DividendsTab.StockDividends,
      title: "Stock Dividends",
      isHidden: !hasStockDividends,
    },
  ];

  if (hasDividends || hasSpecialDividends || hasStockDividends) {
    return (
      <CollapsibleContentSection
        title="Dividends"
        isExpanded={isExpanded}
        onToggleCollapsed={onToggleCollapsed}
        primaryColor={palette.dixie}
        renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Company Financials", "Earnings"]} />}
        renderContentSummary={() => {
          if (hasDividends) {
            return (
              <AmericanDividendsPaidSummary
                tickerSymbol={tickerSymbol}
                dividendsPaidByYear={dividendsPaid.dividendsPaidByYear ?? []}
                dividendYield={dividendsPaid.dividendYieldByTickerSymbol}
              />
            );
          }
          if (hasStockDividends) {
            const [lastStockDividend] = stockDividendsPaid;

            return <StockDividendSummary sharePaid={lastStockDividend.sharePaid} />;
          }

          return null;
        }}
        renderContent={() => (
          <div className="relative rounded bg-white p-3 shadow-lg">
            <Tabs className="py-2" name="DividendsActions" arrayOfTabs={dividendsTabs}>
              {(selected) => {
                return (
                  <div className="contents">
                    {selected.id === DividendsTab.Dividends && (
                      <AmericanDividendsPaidTabContet
                        data={dividendsPaid}
                        loading={loading}
                        filterDividendsByYear={filterDividendsByYear}
                        setFilterDividendsByYear={(year) => setFilterDividendsByYear(year)}
                      />
                    )}
                    {selected.id === DividendsTab.SpecialDividends && (
                      <AmericanSpecialDividendsPaidTabContet
                        data={americanIssuerSpecialDividendsPaid}
                        loading={loading}
                      />
                    )}
                    {selected.id === DividendsTab.StockDividends && (
                      <AmericanStockDividendsPaidTabContet data={stockDividendsPaid} loading={loading} />
                    )}
                  </div>
                );
              }}
            </Tabs>
          </div>
        )}
      />
    );
  }

  return null;
}
